import { motion, AnimatePresence } from 'framer-motion';
import { X, Radio, Siren, Building2, Truck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useGameStore } from '@/state/gameStore';
import { useViewport } from '@/hooks/useViewport';
import type { MainPanelTab } from '@/state/types';
import { MissionsTab } from './tabs/MissionsTab';
import { StationsTab } from './tabs/StationsTab';
import { VehiclesTab } from './tabs/VehiclesTab';

interface MainPanelProps {
  onRequestBuildStation: () => void;
}

const TABS: { id: MainPanelTab; label: string; icon: LucideIcon }[] = [
  { id: 'missions', label: 'Einsätze', icon: Siren },
  { id: 'stations', label: 'Wachen', icon: Building2 },
  { id: 'vehicles', label: 'Fahrzeuge', icon: Truck },
];

/**
 * Slide-in main panel.
 *  - Mobile: full-screen bottom sheet (drag down to dismiss).
 *  - Tablet: 60 % width from the right edge (auto-closes detail panel).
 *  - Desktop: 520 px from the left edge.
 */
export function MainPanel({ onRequestBuildStation }: MainPanelProps) {
  const open = useGameStore((s) => s.mainPanelOpen);
  const closeMainPanel = useGameStore((s) => s.closeMainPanel);
  const tab = useGameStore((s) => s.mainPanelTab);
  const setTab = useGameStore((s) => s.setMainPanelTab);
  const reduceMotion = useGameStore((s) => s.uiPrefs.reduceMotion);
  const viewport = useViewport();

  const dur = reduceMotion ? 0.08 : 0.25;

  // Slide variants per viewport
  const variants = (() => {
    if (viewport === 'mobile') {
      return {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
      };
    }
    if (viewport === 'tablet') {
      return {
        initial: { x: '105%' },
        animate: { x: 0 },
        exit: { x: '105%' },
      };
    }
    return {
      initial: { x: '-105%' },
      animate: { x: 0 },
      exit: { x: '-105%' },
    };
  })();

  const positionClass = (() => {
    if (viewport === 'mobile') {
      return 'fixed inset-x-0 bottom-0 top-12 z-40 rounded-t-2xl';
    }
    if (viewport === 'tablet') {
      return 'fixed bottom-0 right-0 top-0 z-40 w-[60%] border-l border-border-strong';
    }
    return 'fixed bottom-0 left-0 top-0 z-40 w-[520px] border-r border-border-strong';
  })();

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          key="main-panel"
          role="dialog"
          aria-label="Dispatch24 OS"
          initial={variants.initial}
          animate={variants.animate}
          exit={variants.exit}
          transition={{ duration: dur, ease: 'easeInOut' }}
          className={[
            'flex flex-col bg-bg-primary shadow-2xl',
            positionClass,
          ].join(' ')}
        >
          {/* Drag handle on mobile */}
          {viewport === 'mobile' && (
            <div className="flex flex-col items-center pt-2 pb-1">
              <span className="h-1 w-10 rounded-full bg-fg-muted/40" aria-hidden />
            </div>
          )}

          {/* Header */}
          <header className="flex items-center justify-between border-b border-border-subtle bg-bg-secondary px-5 py-4">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-emergency">
                <Radio className="h-4 w-4 text-white" aria-hidden />
              </span>
              <div className="leading-tight">
                <p className="font-display text-lg font-bold">
                  Dispatch<span className="text-accent-emergency">24</span> OS
                </p>
                <p className="text-xs text-fg-muted">Leitstellenübersicht</p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeMainPanel}
              aria-label="Panel schließen"
              className="flex h-10 w-10 min-w-[44px] items-center justify-center rounded-md bg-accent-emergency text-white transition hover:bg-accent-emergency-hover"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </header>

          <nav className="border-b border-border-subtle bg-bg-secondary px-5 pb-4">
            <div className="flex items-center gap-1 rounded-full border border-border-subtle bg-bg-primary p-1">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTab(t.id)}
                    className={[
                      'flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition',
                      active
                        ? 'bg-bg-elevated text-fg-primary shadow-sm'
                        : 'text-fg-muted hover:text-fg-primary',
                    ].join(' ')}
                    aria-pressed={active}
                  >
                    <Icon className="h-3.5 w-3.5" aria-hidden />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </nav>

          <div className="flex-1 overflow-hidden">
            {tab === 'missions' && <MissionsTab />}
            {tab === 'stations' && <StationsTab onRequestBuildStation={onRequestBuildStation} />}
            {tab === 'vehicles' && <VehiclesTab />}
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
